import React, { useState, useEffect } from 'react';
import { getUsers, User, toggleUserSubscription } from '../../api/admin';

const SubscriptionManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getUsers(1, 100, search);
      let filteredUsers = response.users;

      if (filter !== 'all') {
        filteredUsers = filteredUsers.filter(u =>
          filter === 'active' ? u.subscription_active : !u.subscription_active
        );
      }

      setUsers(filteredUsers);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filter, search]);

  const handleToggleSubscription = async (email: string) => {
    try {
      await toggleUserSubscription(email);
      fetchUsers(); // Refresh the list
    } catch (err) {
      setError(`Failed to toggle subscription for ${email}`);
      console.error(err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const activeCount = users.filter(u => u.subscription_active).length;
  const inactiveCount = users.filter(u => !u.subscription_active).length;

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-zinc-400">Loading subscriptions...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 text-zinc-100">Subscription Management</h2>

        {/* Statistics Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="stat-card border-l-4 border-l-emerald-500">
            <div className="stat-card-label">Active</div>
            <div className="stat-card-value text-emerald-400">{activeCount}</div>
          </div>
          <div className="stat-card border-l-4 border-l-rose-500">
            <div className="stat-card-label">Inactive</div>
            <div className="stat-card-value text-rose-400">{inactiveCount}</div>
          </div>
          <div className="stat-card border-l-4 border-l-blue-500">
            <div className="stat-card-label">Total</div>
            <div className="stat-card-value text-blue-400">{users.length}</div>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4 flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email..."
            className="input-dark flex-1 px-4 py-2 rounded-lg"
          />
          <button
            type="submit"
            className="btn-primary px-4 py-2 rounded-lg"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="btn-secondary px-4 py-2 rounded-lg"
            >
              Clear
            </button>
          )}
        </form>

        {/* Filter Buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'all'
                ? 'bg-amber-500 text-zinc-900'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            All Users
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'active'
                ? 'bg-emerald-500 text-white'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            Active Only
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filter === 'inactive'
                ? 'bg-rose-500 text-white'
                : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            Inactive Only
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            {error}
          </div>
        )}

        {/* Subscriptions List */}
        <div className="card-dark rounded-xl overflow-hidden">
          <table className="table-dark">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Member Since</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium text-zinc-200">
                    {user.email}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        user.subscription_active
                          ? 'badge-success'
                          : 'badge-danger'
                      }`}
                    >
                      {user.subscription_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-zinc-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleSubscription(user.email)}
                      className={`text-sm font-medium transition-colors ${
                        user.subscription_active
                          ? 'text-rose-400 hover:text-rose-300'
                          : 'text-emerald-400 hover:text-emerald-300'
                      }`}
                    >
                      {user.subscription_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
